import { alpha } from '@mui/material/styles';

// ----------------------------------------------------------------------

// SETUP COLORS
const GREY = {
  0: '#FFFFFF',
  100: '#F8FAFC',
  200: '#F1F5F9',
  300: '#E2E8F0',
  400: '#CBD5E1',
  500: '#94A3B8',
  600: '#64748B',
  700: '#475569',
  800: '#1E293B',
  900: '#0F172A',
};

const PRIMARY = {
  lighter: '#E0F2FE',
  light: '#38BDF8',
  main: '#0B3B60',       // Mutual Trust Corporate Trust Navy
  dark: '#072740',
  darker: '#041727',
  contrastText: '#FFFFFF',
};

const SECONDARY = {
  lighter: '#D1FAE5',
  light: '#34D399',
  main: '#059669',       // Mutual Trust Growth Emerald
  dark: '#047857',
  darker: '#064E3B',
  contrastText: '#FFFFFF',
};

const INFO = {
  lighter: '#E0F2FE',
  light: '#7DD3FC',
  main: '#0284C7',       // Mutual Trust Accent Blue
  dark: '#0369A1',
  darker: '#075985',
  contrastText: '#FFFFFF',
};

const SUCCESS = {
  lighter: '#DCFCE7',
  light: '#4ADE80',
  main: '#16A34A',
  dark: '#15803D',
  darker: '#14532D',
  contrastText: '#FFFFFF',
};

const WARNING = {
  lighter: '#FEF3C7',
  light: '#FBBF24',
  main: '#D97706',
  dark: '#B45309',
  darker: '#78350F',
  contrastText: '#FFFFFF',
};

const ERROR = {
  lighter: '#FEE2E2',
  light: '#F87171',
  main: '#DC2626',
  dark: '#B91C1C',
  darker: '#7F1D1D',
  contrastText: '#FFFFFF',
};

const lightPalette = {
  mode: 'light',
  common: { black: '#000000', white: '#FFFFFF' },
  primary: PRIMARY,
  secondary: SECONDARY,
  info: INFO,
  success: SUCCESS,
  warning: WARNING,
  error: ERROR,
  grey: GREY,
  divider: alpha(GREY[500], 0.24),
  text: {
    primary: GREY[800],
    secondary: GREY[600],
    disabled: GREY[500],
  },
  background: {
    paper: '#FFFFFF',
    default: GREY[100],
    neutral: GREY[200],
  },
  action: {
    active: GREY[600],
    hover: alpha(GREY[500], 0.08),
    selected: alpha(GREY[500], 0.16),
    disabled: alpha(GREY[500], 0.8),
    disabledBackground: alpha(GREY[500], 0.24),
    focus: alpha(GREY[500], 0.24),
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  },
};

const darkPalette = {
  mode: 'dark',
  common: { black: '#000000', white: '#FFFFFF' },
  primary: {
    lighter: '#E0F2FE',
    light: '#7DD3FC',
    main: '#38BDF8',       // High-visibility cyan/sky accent for dark mode
    dark: '#0284C7',
    darker: '#0B3B60',
    contrastText: '#0F172A',
  },
  secondary: {
    lighter: '#D1FAE5',
    light: '#6EE7B7',
    main: '#10B981',       // High-visibility emerald accent
    dark: '#059669',
    darker: '#064E3B',
    contrastText: '#FFFFFF',
  },
  info: {
    lighter: '#E0F2FE',
    light: '#7DD3FC',
    main: '#38BDF8',
    dark: '#0284C7',
    darker: '#0369A1',
    contrastText: '#FFFFFF',
  },
  success: {
    lighter: '#DCFCE7',
    light: '#86EFAC',
    main: '#22C55E',
    dark: '#16A34A',
    darker: '#15803D',
    contrastText: '#FFFFFF',
  },
  warning: {
    lighter: '#FEF3C7',
    light: '#FCD34D',
    main: '#F59E0B',
    dark: '#D97706',
    darker: '#B45309',
    contrastText: '#0F172A',
  },
  error: {
    lighter: '#FEE2E2',
    light: '#FCA5A5',
    main: '#EF4444',
    dark: '#DC2626',
    darker: '#B91C1C',
    contrastText: '#FFFFFF',
  },
  grey: GREY,
  divider: alpha(GREY[500], 0.2),
  text: {
    primary: '#F8FAFC',
    secondary: '#94A3B8',
    disabled: '#64748B',
  },
  background: {
    paper: '#1E293B',
    default: '#0F172A',
    neutral: '#162032',
  },
  action: {
    active: '#94A3B8',
    hover: alpha(GREY[400], 0.08),
    selected: alpha(GREY[400], 0.16),
    disabled: alpha(GREY[500], 0.4),
    disabledBackground: alpha(GREY[500], 0.24),
    focus: alpha(GREY[400], 0.24),
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  },
};

export default function palette(mode = 'light') {
  return mode === 'dark' ? darkPalette : lightPalette;
}

// Attach default properties for backwards compatibility with imports like `palette.grey`
Object.assign(palette, lightPalette);
